/* Preview.tsx generated by @compiled/babel-plugin v0.36.1 */
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "./Preview.compiled.css";
import { ax, ix } from "@compiled/react/runtime";
// Entry file
/* eslint-disable @atlaskit/editor/no-re-export */

/** @jsxFrag */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import kebabCase from 'lodash/kebabCase';
import { useIntl } from 'react-intl-next';
import Button, { IconButton } from '@atlaskit/button/new';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Toggle from '@atlaskit/toggle';
import Tooltip from '@atlaskit/tooltip';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import { ButtonWithShortcut } from '../../components/ButtonWithShortcut/ButtonWithShortcut';
import { CopyIconButton } from '../../components/CopyIconButton/CopyIconButton';
import { FeedbackForm } from '../../components/FeedbackForm/FeedbackForm';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { Footer } from '../../components/Footer/Footer';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { PromptForm } from '../../components/PromptForm/PromptForm';
import { RetryIconButton } from '../../components/RetryIconButton/RetryIconButton';
import { ActionButtonsLayout } from './components/ActionButtonsLayout';
import { RefineDropdown } from './components/RefineDropdown/RefineDropdown';
import messages from './messages';

// Copied from packages/editor/editor-plugin-ai/src/config-items/config-items.tsx

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */

var hasDiffPreviewStyles = {
  default: "_syaz1lh4 _80om13gf"
};
var columnLayout = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var interrogationWrapper = null;
var defaultPaddingStyles = null;
var previewContainerPaddingCompact = null;

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
var previewContainerBorders = null;
var previewFloatingContainerStyle = null;
var mapActionAppearanceToButtonAppearance = {
  primary: 'primary',
  secondary: 'default',
  cancel: 'subtle'
};
export var PreviewScreen = function PreviewScreen(_ref) {
  var parentPresetPromptLabel = _ref.parentPresetPromptLabel,
    childShortTitle = _ref.childShortTitle,
    staticPromptTitle = _ref.staticPromptTitle,
    staticInsertedPrompt = _ref.staticInsertedPrompt,
    latestPrompt = _ref.latestPrompt,
    preview = _ref.preview,
    diffPreview = _ref.diffPreview,
    canShowVisualDiffPreview = _ref.canShowVisualDiffPreview,
    _ref$disableActions = _ref.disableActions,
    disableActions = _ref$disableActions === void 0 ? false : _ref$disableActions,
    _ref$disableInterroga = _ref.disableInterrogation,
    disableInterrogation = _ref$disableInterroga === void 0 ? false : _ref$disableInterroga,
    onSubmit = _ref.onSubmit,
    onCancel = _ref.onCancel,
    onCopy = _ref.onCopy,
    onRetry = _ref.onRetry,
    onInputFocus = _ref.onInputFocus,
    onInputBlur = _ref.onInputBlur,
    onInputChange = _ref.onInputChange,
    onADFChange = _ref.onADFChange,
    onGoodResponseButtonClick = _ref.onGoodResponseButtonClick,
    onBadResponseButtonClick = _ref.onBadResponseButtonClick,
    onEditPromptClick = _ref.onEditPromptClick,
    onBack = _ref.onBack,
    onShowDiffPreview = _ref.onShowDiffPreview,
    onClose = _ref.onClose,
    _ref$showBack = _ref.showBack,
    showBack = _ref$showBack === void 0 ? true : _ref$showBack,
    applications = _ref.applications,
    _ref$secondaryActions = _ref.secondaryActions,
    secondaryActions = _ref$secondaryActions === void 0 ? [] : _ref$secondaryActions,
    isInputActive = _ref.isInputActive,
    inputValue = _ref.inputValue,
    inputADFValue = _ref.inputADFValue,
    PromptEditor = _ref.PromptEditor,
    clearInputRef = _ref.clearInputRef,
    providerFactory = _ref.providerFactory,
    schema = _ref.schema,
    disclaimer = _ref.disclaimer,
    agent = _ref.agent,
    onRefinementClick = _ref.onRefinementClick,
    refineDropdownItems = _ref.refineDropdownItems,
    _ref$hideHorizontalRu = _ref.hideHorizontalRule,
    hideHorizontalRule = _ref$hideHorizontalRu === void 0 ? false : _ref$hideHorizontalRu,
    _ref$showBetaLozenge = _ref.showBetaLozenge,
    showBetaLozenge = _ref$showBetaLozenge === void 0 ? false : _ref$showBetaLozenge,
    _ref$isClearTitle = _ref.isClearTitle,
    isClearTitle = _ref$isClearTitle === void 0 ? false : _ref$isClearTitle;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    forceFocusRing = _useState2[0],
    setForceFocusRing = _useState2[1];
  var presetTitleString = staticPromptTitle !== null && staticPromptTitle !== void 0 ? staticPromptTitle : latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.presetTitle;
  var promptString = staticInsertedPrompt !== null && staticInsertedPrompt !== void 0 ? staticInsertedPrompt : latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput;
  var firePrimaryApplication = function firePrimaryApplication() {
    if (!applications) {
      return;
    }
    setForceFocusRing(false);
    applications[applications.length - 1].onClick();
  };
  var onInterrogateBlur = useCallback(function () {
    setForceFocusRing(false);
  }, []);
  var handleOnInputFocus = useCallback(function () {
    setForceFocusRing(true);
    if (onInputFocus) {
      onInputFocus();
    }
  }, [onInputFocus]);
  var focusInputRef = useRef(undefined);
  var initialPlaceholder = agent ? formatMessage(messages.placeholderWithAgent, {
    agentName: agent.name
  }) : formatMessage(messages.defaultInputPlaceholder);
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isDiffPreviewVisible = _useState4[0],
    setIsDiffPreviewVisible = _useState4[1];
  var hasValidDiffPreview = !!diffPreview;
  var handleToggleDiffPreviewVisibility = useCallback(function () {
    setIsDiffPreviewVisible(function (value) {
      onShowDiffPreview === null || onShowDiffPreview === void 0 || onShowDiffPreview(!value);
      return !value;
    });
  }, [setIsDiffPreviewVisible, onShowDiffPreview]);
  var enableLinksInPromptEditor = !!fg('platform_editor_ai_interrogate_with_convo_ai');

  // This is used to determine when to disable a number of features to prevent
  // the user from performing actions on previous responses
  var isViewingLatestResponse = !disableActions && !disableInterrogation;
  var actionButtons = useMemo(function () {
    var secondaryButtons = !disableActions && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FeedbackForm, {
      onBadResponseButtonClick: onBadResponseButtonClick,
      onGoodResponseButtonClick: onGoodResponseButtonClick
    }), onRetry && /*#__PURE__*/React.createElement(RetryIconButton, {
      spacing: "compact",
      appearance: "subtle",
      onClick: onRetry
    }), onCopy && /*#__PURE__*/React.createElement(CopyIconButton, {
      spacing: "compact",
      appearance: "subtle",
      onClick: onCopy
    }), secondaryActions.map(function (action) {
      var _action$analyticsTitl;
      var id = kebabCase((_action$analyticsTitl = action.analyticsTitle) !== null && _action$analyticsTitl !== void 0 ? _action$analyticsTitl : action.label);
      return /*#__PURE__*/React.createElement(Tooltip, {
        key: id,
        content: action.label,
        position: "top"
      }, /*#__PURE__*/React.createElement(IconButton, {
        testId: id,
        type: "button",
        onClick: action.onClick,
        icon: action.icon,
        label: action.label,
        appearance: "subtle",
        spacing: "compact"
      }));
    }));
    var cancelButton = onCancel ? /*#__PURE__*/React.createElement(Button, {
      testId: "discard-button",
      onClick: onCancel,
      type: "button",
      appearance: disableActions ? 'primary' : 'subtle',
      autoFocus: disableActions || applications.length === 0
    }, formatMessage(messages.discardLabel)) : undefined;
    var primaryButtons = !disableActions && applications.map(function (_ref2, index) {
      var onClick = _ref2.onClick,
        label = _ref2.label,
        appearance = _ref2.appearance,
        shortcut = _ref2.shortcut,
        isDisabled = _ref2.isDisabled,
        tooltip = _ref2.tooltip;
      var button = /*#__PURE__*/React.createElement(ButtonWithShortcut, {
        key: label,
        onClick: onClick,
        type: "button",
        shortcut: shortcut,
        autoFocus: appearance === 'primary',
        forceFocusRing: index === applications.length - 1 && forceFocusRing,
        buttonAutoFocusRing: index === applications.length - 1 && !forceFocusRing,
        appearance: mapActionAppearanceToButtonAppearance[appearance],
        isDisabled: isDisabled
      }, label);
      if (tooltip) {
        return /*#__PURE__*/React.createElement(Tooltip, {
          key: "".concat(label, "-tooltip"),
          content: tooltip,
          position: "top"
        }, button);
      }
      return button;
    });
    if (primaryButtons && !agent && refineDropdownItems) {
      primaryButtons.unshift( /*#__PURE__*/React.createElement(RefineDropdown, {
        key: "refine-dropdown",
        items: refineDropdownItems,
        onClick: onRefinementClick
      }));
    }
    return /*#__PURE__*/React.createElement(ActionButtonsLayout, {
      cancelButton: cancelButton,
      secondaryButtons: secondaryButtons,
      primaryButtons: primaryButtons
    });
  }, [disableActions, onBadResponseButtonClick, onGoodResponseButtonClick, onRetry, onCopy, secondaryActions, onCancel, applications, formatMessage, agent, refineDropdownItems, forceFocusRing, onRefinementClick]);
  return /*#__PURE__*/React.createElement("div", {
    /* eslint-disable @atlaskit/ui-styling-standard/no-classname-prop */
    className: ax(["_zulp1b66 _1e0c1txw _2lx21bp4 _4cvr1y6m", "preview-screen"])
    /* eslint-enable */
  }, /*#__PURE__*/React.createElement(FloatingContainer, {
    disablePadding: true,
    "data-testid": "preview-screen"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: ax(["_1e0c1txw _2lx21bp4", "preview-screen ".concat(AI_MODAL_SCREEN_CLASS)]),
    rainbowBorder: false
  }, /*#__PURE__*/React.createElement(VisuallyHidden, null, formatMessage(messages.previewScreenLabel)), /*#__PURE__*/React.createElement(LatestPromptHeader, {
    parentPresetPromptLabel: parentPresetPromptLabel,
    childShortTitle: childShortTitle,
    prompt: promptString,
    promptADF: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput,
    presetTitle: presetTitleString,
    agent: agent,
    providerFactory: providerFactory,
    schema: schema,
    onEditPromptClick: onEditPromptClick,
    showBack: showBack,
    onBack: onBack,
    onClose: onClose,
    isClearTitle: isClearTitle
  }), /*#__PURE__*/React.createElement("div", {
    className: ax(["_ca0qutpp _u5f3utpp _n3tdutpp _19bvutpp", hideHorizontalRule ? "_ca0qze3t" : "_x3dopp12 _n7zlpp12"]),
    style: {
      "--_18x9sjc": ix("1px solid ".concat("var(--ds-border, #091E4224)"))
    }
  }, /*#__PURE__*/React.createElement(Stack
  // Ignored via go/ees005
  // eslint-disable-next-line react/jsx-props-no-spreading
  , {
    space: 'space.200'
  }, canShowVisualDiffPreview &&
  /*#__PURE__*/
  // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
  React.createElement(Tooltip, {
    content: !hasValidDiffPreview && formatMessage(messages.highlightDifferencesDisabledTooltip)
  }, /*#__PURE__*/React.createElement(Inline, {
    alignBlock: "center"
  }, /*#__PURE__*/React.createElement(Toggle, {
    isDisabled: !hasValidDiffPreview,
    label: formatMessage(messages.highlightDifferencesLabel),
    id: "diff-preview-toggle",
    testId: "diff-preview-toggle",
    isChecked: isDiffPreviewVisible && hasValidDiffPreview,
    onChange: handleToggleDiffPreviewVisibility
  }), /*#__PURE__*/React.createElement(Box, {
    xcss: !hasValidDiffPreview && hasDiffPreviewStyles.default,
    as: "label",
    htmlFor: "diff-preview-toggle"
  }, /*#__PURE__*/React.createElement(Text, null, formatMessage(messages.highlightDifferencesLabel))))), canShowVisualDiffPreview && isDiffPreviewVisible && hasValidDiffPreview ? diffPreview : preview, actionButtons)), isViewingLatestResponse && PromptEditor && /*#__PURE__*/React.createElement("div", {
    className: ax(["_ca0qutpp _u5f3utpp _n3tdutpp _19bvutpp", "_1tke1psl _vchhusvi _247hidpf _txhsidpf _zu32idpf _16d6idpf"]),
    style: {
      "--_1fdw31o": ix("calc(".concat("var(--ds-space-600, 48px)", " + 0.5px)"))
    }
  }, isViewingLatestResponse && /*#__PURE__*/React.createElement(PromptForm, {
    type: "interrogate",
    showButtons: isInputActive,
    autoFocus: true,
    placeholder: initialPlaceholder,
    value: inputValue,
    adfValue: inputADFValue
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    onFormSubmit: onSubmit
    // Ignored via go/ees005
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ,
    onInputChange: onInputChange,
    onADFChange: onADFChange,
    onInputFocus: handleOnInputFocus,
    focusInputRef: focusInputRef,
    clearInputRef: clearInputRef,
    PromptEditor: PromptEditor,
    enableLinks: enableLinksInPromptEditor,
    fireParentActionButton: firePrimaryApplication,
    focusParentActionButton: setForceFocusRing,
    showLogo: false,
    onInputBlur: onInterrogateBlur
  })), /*#__PURE__*/React.createElement(Footer, {
    brand: agent ? 'rovo' : 'atlassian-intelligence',
    disclaimer: disclaimer,
    showDisclaimer: true,
    showBetaLozenge: showBetaLozenge
  })));
};